import { useAuthData } from '@auth/features/auth';
import { useNavigate } from '@tanstack/react-router';
import { MessageSquare } from 'lucide-react';
import { Badge, Button, Tooltip } from '@/shared/ui';
import { usePendingCommentCount } from '../api/use-pending-comment-count';
import css from './pending-comments-trigger.module.scss';

/** Two digits keep the badge inside the control; beyond that it reads "99+". */
const BADGE_MAX = 99;

type PendingCommentsTriggerProps = {
  className?: string;
};

/**
 * Header messaging icon (XT-902): how many Middle-layer comments on the
 * consultant's own itineraries still wait for them — cleared by a reply in the
 * comment's thread or by marking it handled. Opens the itineraries list
 * narrowed to their itineraries with pending comments. Sits beside the bell in
 * both headers, so it owns no layout.
 */
export const PendingCommentsTrigger = ({
  className,
}: PendingCommentsTriggerProps) => {
  const { data: authData } = useAuthData();
  const accountId = authData?.user.id ?? null;
  const canRead =
    authData?.user.permissions.includes('itineraries:read') ?? false;
  const { data } = usePendingCommentCount(canRead && accountId != null);
  const navigate = useNavigate();

  if (!canRead || !accountId) return null;

  const count = data?.count ?? 0;
  const label =
    count > 0
      ? `Messages, ${count} awaiting your reply`
      : 'Messages, none awaiting your reply';

  return (
    <Tooltip content="Messages awaiting your reply" position="bottom">
      <div className={css.root}>
        <Button
          aria-label={label}
          className={className}
          icon={<MessageSquare className={css.icon} />}
          onClick={() =>
            void navigate({
              to: '/itineraries',
              search: { pendingComments: true, owners: [accountId] },
            })
          }
          size="icon"
          variant="ghost"
        />
        {count > 0 ? (
          <span className={css.badgeAnchor}>
            <Badge variant="error">
              {count > BADGE_MAX ? `${BADGE_MAX}+` : count}
            </Badge>
          </span>
        ) : null}
      </div>
    </Tooltip>
  );
};
